/**
 * Pivot Points Tooltip
 *
 * Displays the 7 pivot levels:
 * - PP (Pivot Point)
 * - R1, R2, R3 (Resistance levels)
 * - S1, S2, S3 (Support levels)
 */

import { format } from "d3-format";
import React, { Component } from "react";
import PropTypes from "prop-types";

import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";
import { functor } from "../utils";

class PivotPointsTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }

  renderSVG(moreProps) {
    const {
      onClick,
      origin: originProp,
      className,
      fontFamily,
      fontSize,
      labelFill,
      textFill,
      displayFormat,
      yAccessor,
      options,
      appearance,
      labels,
    } = this.props;

    const origin = functor(originProp);
    const {
      chartConfig: { width, height },
    } = moreProps;
    const [x, y] = origin(width, height);

    const currentItem = this.props.displayValuesFor(this.props, moreProps);
    const value = currentItem && yAccessor(currentItem);

    // Format values with n/a fallback
    const pivotValue =
      value && Number.isFinite(value.pivot)
        ? displayFormat(value.pivot)
        : "n/a";
    const r1Value =
      value && Number.isFinite(value.r1) ? displayFormat(value.r1) : "n/a";
    const r2Value =
      value && Number.isFinite(value.r2) ? displayFormat(value.r2) : "n/a";
    const r3Value =
      value && Number.isFinite(value.r3) ? displayFormat(value.r3) : "n/a";
    const s1Value =
      value && Number.isFinite(value.s1) ? displayFormat(value.s1) : "n/a";
    const s2Value =
      value && Number.isFinite(value.s2) ? displayFormat(value.s2) : "n/a";
    const s3Value =
      value && Number.isFinite(value.s3) ? displayFormat(value.s3) : "n/a";

    const type = options?.type || "standard";
    let period = options?.period;

    // Always detect the actual period being used (period is always auto)
    if ((!period || period === "auto") && currentItem) {
      // Detect bar interval from plotData
      const { plotData } = moreProps;
      if (plotData && plotData.length >= 2) {
        let totalInterval = 0;
        let count = 0;
        const samplesToCheck = Math.min(5, plotData.length - 1);

        for (let i = 0; i < samplesToCheck; i++) {
          const curr = plotData[i + 1];
          const prev = plotData[i];
          if (curr.date && prev.date) {
            const interval =
              new Date(curr.date).getTime() - new Date(prev.date).getTime();
            if (interval > 0) {
              totalInterval += interval;
              count++;
            }
          }
        }

        const avgInterval = count > 0 ? totalInterval / count : 0;
        if (avgInterval <= 15 * 60 * 1000) {
          period = "daily";
        } else if (avgInterval < 24 * 60 * 60 * 1000) {
          period = "weekly";
        } else {
          period = "monthly";
        }
      } else {
        period = "daily"; // fallback
      }
    } else if (!period) {
      period = "daily"; // fallback
    }

    // Get colors from appearance
    const pivotColor = appearance?.strokePivot?.color || "#9C27B0";
    const r1Color = appearance?.strokeR1?.color || "#B82C0B";
    const r2Color = appearance?.strokeR2?.color || "#E36460";
    const r3Color = appearance?.strokeR3?.color || "#FFD0CF";
    const s1Color = appearance?.strokeS1?.color || "#699158";
    const s2Color = appearance?.strokeS2?.color || "#B3D987";
    const s3Color = appearance?.strokeS3?.color || "#D3E8AE";

    // Get labels from props (passed from registry) or use defaults
    const pivotLabel = labels?.pivot || "PP";
    const r1Label = labels?.r1 || "R1";
    const r2Label = labels?.r2 || "R2";
    const r3Label = labels?.r3 || "R3";
    const s1Label = labels?.s1 || "S1";
    const s2Label = labels?.s2 || "S2";
    const s3Label = labels?.s3 || "S3";

    // Type and period label mapping
    const typeLabel = type === "fibonacci" ? "fibonacci" : "padrão";
    const periodLabel =
      period === "monthly"
        ? "mensal"
        : period === "weekly"
          ? "semanal"
          : "diário";

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-pivot-points"
      >
        <ToolTipText
          fontFamily={fontFamily}
          fontSize={fontSize}
          fill={textFill}
        >
          <ToolTipTSpanLabel fill={labelFill}>Pivot</ToolTipTSpanLabel>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{` (${typeLabel}, ${periodLabel})`}</ToolTipTSpanLabel>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`  ${pivotLabel}: `}</ToolTipTSpanLabel>
          <tspan fill={pivotColor}>{pivotValue}</tspan>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`  ${r1Label}: `}</ToolTipTSpanLabel>
          <tspan fill={r1Color}>{r1Value}</tspan>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`  ${r2Label}: `}</ToolTipTSpanLabel>
          <tspan fill={r2Color}>{r2Value}</tspan>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`  ${r3Label}: `}</ToolTipTSpanLabel>
          <tspan fill={r3Color}>{r3Value}</tspan>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`  ${s1Label}: `}</ToolTipTSpanLabel>
          <tspan fill={s1Color}>{s1Value}</tspan>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`  ${s2Label}: `}</ToolTipTSpanLabel>
          <tspan fill={s2Color}>{s2Value}</tspan>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`  ${s3Label}: `}</ToolTipTSpanLabel>
          <tspan fill={s3Color}>{s3Value}</tspan>
        </ToolTipText>
      </g>
    );
  }

  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

PivotPointsTooltip.propTypes = {
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  labelFill: PropTypes.string,
  textFill: PropTypes.string,
  displayFormat: PropTypes.func.isRequired,
  displayValuesFor: PropTypes.func,
  onClick: PropTypes.func,
  yAccessor: PropTypes.func,
  options: PropTypes.object,
  appearance: PropTypes.object,
  labels: PropTypes.shape({
    pivot: PropTypes.string,
    r1: PropTypes.string,
    r2: PropTypes.string,
    r3: PropTypes.string,
    s1: PropTypes.string,
    s2: PropTypes.string,
    s3: PropTypes.string,
  }),
};

PivotPointsTooltip.defaultProps = {
  origin: [0, 10],
  className: "react-stockcharts-tooltip react-stockcharts-pivot-points-tooltip",
  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
  fontSize: 11,
  labelFill: "#4682B4",
  textFill: "#000000",
  displayFormat: format(".2f"),
  displayValuesFor: displayValuesFor,
  onClick: undefined,
};

export default PivotPointsTooltip;
